import React, { useEffect, useMemo, useState } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import Loader from "../../components/Loader";
import { apiFetch } from "../../utils/api";

const initialForm = {
  id: "0",
  branchId: "",
  startTime: "07:00",
  endTime: "08:30",
  price: "",
  activo: true,
};

const normalizeTime = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5);
};

const alertMessage = (message) => {
  if (window.notie?.alert) {
    window.notie.alert({ type: "warning", text: message, time: 4 });
  } else {
    window.alert(message);
  }
};

const TarifasCanchas = () => {
  const [branches, setBranches] = useState([]);
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedBranchRates = useMemo(() => {
    return rates.filter((rate) => !form.branchId || rate.branchId === form.branchId);
  }, [rates, form.branchId]);

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const [branchesResponse, ratesResponse] = await Promise.all([
        apiFetch("/api/branches"),
        apiFetch("/api/court-rates"),
      ]);

      if (!branchesResponse.status) throw new Error(branchesResponse.message || "No se pudieron cargar las sucursales.");
      if (!ratesResponse.status) throw new Error(ratesResponse.message || "No se pudieron cargar las tarifas.");

      const nextBranches = Array.isArray(branchesResponse.data) ? branchesResponse.data : [];
      const nextRates = Array.isArray(ratesResponse.data) ? ratesResponse.data : [];
      setBranches(nextBranches);
      setRates(nextRates);
      setForm((current) => ({
        ...current,
        branchId: current.branchId || nextBranches[0]?.id || "",
      }));
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo cargar la configuracion de tarifas.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm({
      ...initialForm,
      branchId: form.branchId || branches[0]?.id || "",
    });
  };

  const saveRate = async () => {
    if (!form.branchId || !form.startTime || !form.endTime || form.price === "") {
      alertMessage("Completa sucursal, horario y precio.");
      return;
    }

    setIsSaving(true);
    try {
      const isNew = form.id === "0";
      const response = await apiFetch(isNew ? "/api/court-rates" : `/api/court-rates/${form.id}`, {
        method: isNew ? "POST" : "PUT",
        body: JSON.stringify({
          branchId: form.branchId,
          startTime: form.startTime,
          endTime: form.endTime,
          price: Number(form.price),
          activo: Boolean(form.activo),
        }),
      });

      if (!response.status) {
        throw new Error(response.message || "No se pudo guardar la tarifa.");
      }
      resetForm();
      await loadData();
    } catch (err) {
      console.error(err);
      alertMessage(err.message || "No se pudo guardar la tarifa.");
    } finally {
      setIsSaving(false);
    }
  };

  const editRate = (rate) => {
    setForm({
      id: rate.id,
      branchId: rate.branchId,
      startTime: normalizeTime(rate.startTime),
      endTime: normalizeTime(rate.endTime),
      price: String(rate.price ?? ""),
      activo: Boolean(rate.activo),
    });
  };

  const deleteRate = async (id) => {
    if (!window.confirm("¿Eliminar esta tarifa?")) return;
    try {
      const response = await apiFetch(`/api/court-rates/${id}`, { method: "DELETE" });
      if (!response.status) throw new Error(response.message || "No se pudo eliminar la tarifa.");
      await loadData();
    } catch (err) {
      console.error(err);
      alertMessage(err.message || "No se pudo eliminar la tarifa.");
    }
  };

  return (
    <MasterLayout>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {error && <div className="alert alert-warning">{error}</div>}
          <div className="card h-100 p-0 radius-12 overflow-hidden mb-24">
            <div className="card-body p-32">
              <div className="d-flex align-items-center justify-content-between mb-24">
                <div>
                  <h5 className="mb-4">Tarifas por horario</h5>
                  <p className="text-secondary-light mb-0">
                    Configura precios por sucursal. Aplican a todas las canchas de la sucursal.
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-4 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">Sucursal</label>
                  <select name="branchId" value={form.branchId} onChange={handleChange} className="form-control radius-8">
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-2 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">Hora inicial</label>
                  <input name="startTime" type="time" value={form.startTime} onChange={handleChange} className="form-control radius-8" />
                </div>
                <div className="col-sm-2 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">Hora final</label>
                  <input name="endTime" type="time" value={form.endTime} onChange={handleChange} className="form-control radius-8" />
                </div>
                <div className="col-sm-2 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">Precio</label>
                  <input name="price" value={form.price} onChange={handleChange} className="form-control radius-8" placeholder="0.00" />
                </div>
                <div className="col-sm-2 mb-20 d-flex align-items-end">
                  <div className="form-switch switch-primary d-flex align-items-center gap-2">
                    <input className="form-check-input" type="checkbox" role="switch" id="activo" name="activo" checked={form.activo} onChange={handleChange} />
                    <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="activo">Activo</label>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                {form.id !== "0" && (
                  <button type="button" onClick={resetForm} className="btn btn-outline-secondary">
                    Cancelar edicion
                  </button>
                )}
                <button type="button" disabled={isSaving} onClick={saveRate} className="btn btn-primary">
                  {form.id === "0" ? "Agregar tarifa" : "Guardar tarifa"}
                </button>
              </div>
            </div>
          </div>

          <div className="card basic-data-table">
            <div className="card-header">
              <h5 className="card-title mb-0">Rangos configurados</h5>
            </div>
            <div className="card-body" style={{ overflowX: "auto" }}>
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th>SUCURSAL</th>
                    <th>HORARIO</th>
                    <th>PRECIO</th>
                    <th>ACTIVO</th>
                    <th>ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBranchRates.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">No hay tarifas configuradas.</td>
                    </tr>
                  ) : selectedBranchRates.map((rate) => (
                    <tr key={rate.id}>
                      <td>{rate.branch?.nombre || branches.find((branch) => branch.id === rate.branchId)?.nombre || ""}</td>
                      <td>{normalizeTime(rate.startTime)} - {normalizeTime(rate.endTime)}</td>
                      <td>${Number(rate.price || 0).toFixed(2)}</td>
                      <td>{rate.activo ? "Si" : "No"}</td>
                      <td>
                        <button type="button" onClick={() => editRate(rate)} className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0">
                          <i className="ri-edit-line" style={{ fontSize: 16 }} />
                        </button>
                        <button type="button" onClick={() => deleteRate(rate.id)} className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0">
                          <i className="ri-delete-bin-2-line" style={{ fontSize: 16 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </MasterLayout>
  );
};

export default TarifasCanchas;
